import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomer } from '../../../contexts/CustomerContext';
import useQueryString from '../../../hooks/use-query-string';
import useOrderPackage from '../../../hooks/useOrderPackage';
import useOfferCoupon from '../../../hooks/useOfferCoupon';
import useCart from '../../../hooks/useCart';
import usePoses from '../../../hooks/usePoses';
import useBackgrounds from '../../../hooks/useBackgrounds';
import useImageOrientations from '../../../hooks/useImageOrientations';
import QueryKeys from '../../../utils/QueryKeys';
import { routes } from '../../../routes';
import { freeAddonsOf, freeItemLabel } from '../../../utils/offerPricing';
import { formatDeadline } from '../../../utils/offerDeadline';
import { addFreeItem } from '../../../utils/freeMagnets';
import { shouldShowFreeItem } from '../_ElementaryMultiPose/FreeMagnets/freeMagnetsVisibility';
import Item, { canRenderPreview } from '../Holiday/Builder/Item';
import GiftTile from '../Holiday/Builder/GiftTile';
import ItemSubtitle from '../Holiday/Builder/ItemSubtitle';
import { selectableDesigns, defaultPoseFor, readyToDecide } from './freeItemPage';

/**
 * "Your FREE <item>" -- the free-item picker (#5747), laid out EXACTLY like the
 * Announcements & Gifts shelf (Katie, 2026-10-18): one GiftTile per design in
 * the set, the child's photo on each through the gifts renderer, the price
 * struck through to Free. Pressing a tile claims it -- the sheet is granted
 * with the child's favourite photo -- and goes straight to the gifts Edit
 * screen for that sheet, the same screen a bought gift goes to, where the
 * parent picks the pose (and a background, where the design takes one) and
 * continues as they normally would.
 *
 * Shown once PER PACKAGE, after the build. Gate: shouldShowFreeItem (the ONE
 * gate in freeMagnetsVisibility), re-asked here against a FRESH cart, and
 * skipped in an effect (never during render) so it cannot double-fire under
 * Strict Mode. A child with no usable photo for ANY design in the set is not
 * shown the page -- empty is a valid answer.
 */
export default function FreeItem() {
  const { orderPackageId } = useParams();
  const { next } = useQueryString();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { customer } = useCustomer();

  const schoolId = customer?.currentSchool?.id;
  const cartId = customer?.cart?.id;

  const { data: orderPackage, isLoading: loadingPackage } = useOrderPackage(orderPackageId);
  const packageId = orderPackage?.packageId;
  // THIS package's child, not whoever the session is currently on: a second
  // child's package built in the same session must get the second child's photos.
  const studentId = orderPackage?.studentId || customer?.currentStudent?.id;
  const { data: offer, isLoading: loadingOffer } = useOfferCoupon(
    customer?.currentStudent?.studentId, schoolId, packageId,
  );
  // Fresh on every mount: the per-package list flips on accept and the
  // app-wide default is refetchOnMount: false.
  const { data: cart, isLoading: loadingCart } = useCart(cartId, { refetchOnMount: 'always' });
  const { data: poses, isPlaceholderData: posesPlaceholder } = usePoses(studentId, packageId);
  const { data: backgrounds, isPlaceholderData: backgroundsPlaceholder } = useBackgrounds(packageId);

  // Orientation is MEASURED, never read off a filename (season x crop).
  const urls = useMemo(() => (poses || []).map((p) => p?.watermarkUrl || p?.imageUrl).filter(Boolean), [poses]);
  const orientations = useImageOrientations(urls);

  const addons = freeAddonsOf(offer);
  const designs = useMemo(() => selectableDesigns(addons, poses, orientations), [addons, poses, orientations]);

  const [claiming, setClaiming] = useState(null);
  const [error, setError] = useState(null);

  const favouritePoseId = orderPackage?.sheets?.[0]?.poseId;
  const label = freeItemLabel(offer);
  const deadline = formatDeadline(offer?.expiresOn);

  const continueOn = () => history.push(next || routes.confirm(orderPackageId));

  const ready = readyToDecide({
    loadingPackage, packageId, loadingOffer, loadingCart, poses, posesPlaceholder, backgroundsPlaceholder,
  });
  const show = ready && designs.length > 0 && shouldShowFreeItem({
    offer, orderPackageId, freeItemPackages: cart?.freeItemPackages, allJpg: false,
  });
  const shouldSkip = ready && !show;
  useEffect(() => {
    if (shouldSkip) continueOn();
  }, [shouldSkip]);

  if (!ready || shouldSkip) return null;

  // Claim a design: grant the sheet with the child's favourite usable photo,
  // then hand the parent the gifts Edit screen for that one sheet -- pose and
  // background are chosen there, exactly as for a gift they bought.
  const claim = async (design) => {
    const pose = defaultPoseFor(design, favouritePoseId);
    if (!pose) return;
    setClaiming(design.addon.id);
    setError(null);
    try {
      const result = await addFreeItem({
        cartId, orderPackageId, addonId: design.addon.id, poseId: pose.id, backgroundId: undefined,
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CART] });
      if (!result?.applied || !result?.addon_sheet_id) {
        console.warn('Free item not applied:', result?.reason); // eslint-disable-line no-console
        continueOn();
        return;
      }
      history.push(routes.holiday.edit(orderPackageId, { s: [result.addon_sheet_id] }));
    } catch (err) {
      console.error('Free item failed:', err); // eslint-disable-line no-console
      setError(`Something went wrong adding your free ${label}. Please try again.`);
      setClaiming(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 pb-12 pt-7 md:px-6">
      <div className="text-center">
        {/* Brand blue, not red (Katie, 2026-10-18). */}
        <span
          className="inline-block rounded-full px-3.5 py-1.5 text-xs font-extrabold uppercase tracking-widest text-white"
          style={{ backgroundColor: '#008fcf' }}
        >
          Included with your package
        </span>
        <h1 className="mt-4 text-3xl font-extrabold leading-tight text-gray-900">{`Your FREE ${label}`}</h1>
        <p className="mx-auto mt-3 max-w-md text-base leading-snug text-gray-600">
          {designs.length > 1 ? 'Pick the one you want' : 'Tap it to add it'}
          {deadline ? ` — included with your package until ${deadline}` : ''}
          . You will choose the photo next.
        </p>
        {error && <p className="mt-3 text-sm font-semibold text-red-700">{error}</p>}
      </div>

      {/* The gifts shelf's own grid, tile for tile (GiftShop.jsx). */}
      <div
        className={'mt-8 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-6 '
          + 'md:gap-y-10 lg:grid-cols-4 lg:gap-x-11 lg:gap-y-14'}
        role="list"
        aria-label="Your free item"
        aria-busy={!!claiming}
      >
        {designs.map((design) => {
          const { addon } = design;
          const tilePose = defaultPoseFor(design, favouritePoseId);
          return (
            <div key={addon.id} role="listitem" className={claiming && claiming !== addon.id ? 'opacity-50' : undefined}>
              <GiftTile
                name={addon.name}
                priceNode={(
                  <ItemSubtitle
                    price={addon.priceWith}
                    addon={addon}
                    offer={offer}
                    grantedCount={1}
                    showOfferNote={false}
                  />
                )}
                preview={canRenderPreview(addon, addon.cssName) ? (
                  <Item
                    previewOnly
                    addon={addon}
                    variant={addon.cssName}
                    background={addon.backgroundSelection ? (backgrounds || [])[0] : undefined}
                    pose={tilePose}
                  />
                ) : null}
                imageUrl={addon.imageUrl}
                isAdded={false}
                onCustomize={() => { if (!claiming) claim(design); }}
              />
            </div>
          );
        })}
      </div>

      <div className="mx-auto mt-10 max-w-sm text-center">
        <button
          type="button"
          disabled={!!claiming}
          onClick={continueOn}
          className="border-0 bg-transparent text-sm font-semibold text-gray-500 hover:text-gray-900 hover:underline"
        >
          No thanks, continue without it
        </button>
      </div>
    </div>
  );
}
